// Edit day (owner, 2026-11-03): the one dialog for a whole day's clock times,
// credited hours and project time. HR and a person's manager change it
// directly; the person asks, and an approver approves, adjusts or declines.
// The server decides who may do what (day_edit_state's `mode`) and holds
// every rule (20261103120000_edit_day.sql); this only lays the day out.
import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Button, IconButton, Mono, Select } from './primitives';
import { Card } from './card';
import { Dialog } from './dialog';
import { DateField, Field, TextArea, TextInput } from './field';
import { InlineStatus, LoadError, useToast } from './feedback';
import { DecisionRow, KindTag, YOURS } from './requests';
import { fmtDayKey, fmtRelative } from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import { fmtHours, listCategories, type Category } from '../lib/compHours';
import {
  decideDayEdit,
  editDay,
  getDayState,
  listPendingDayEdits,
  requestDayEdit,
  sessionProblem,
  sessionsText,
  spanSeconds,
  type DayChange,
  type DayState,
  type PendingDayEdit,
} from '../lib/editDay';

type SessionRow = { key: string; id?: string; in: string; out: string };
type CreditRow = {
  key: string;
  id?: string;
  from: string;
  to: string;
  hours: string;
  category: string;
  note: string | null;
};
type ProjectRow = { project_id: string; task_id: string | null; label: string; minutes: string };
type Form = { sessions: SessionRow[]; credits: CreditRow[]; projects: ProjectRow[] };

let seq = 0;
const key = () => `n${++seq}`;
const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
/** Hours between From and To, to the half hour; null until both are set. */
const spanHours = (from: string, to: string): number | null =>
  from && to ? Math.round((mins(to) - mins(from)) / 30) / 2 : null;

/** The form a day (and, when deciding, the change asked for) starts from. */
function formOf(day: DayState, asked?: DayChange): Form {
  const logged = day.sessions.filter((s) => s.status === 'logged');
  return {
    sessions: (
      asked?.sessions ?? logged.map((s) => ({ id: s.id, in: s.in, out: s.out ?? '' }))
    ).map((s) => ({ key: key(), id: s.id, in: s.in, out: s.out })),
    credits: (
      asked?.credits ??
      day.credits
        .filter((c) => c.status === 'approved')
        .map((c) => ({ id: c.id, hours: c.hours, category: c.category, note: c.note }))
    ).map((c) => ({
      key: key(),
      id: c.id,
      from: '',
      to: '',
      hours: String(c.hours),
      category: c.category,
      note: c.note ?? null,
    })),
    projects: day.projects.map((p) => ({
      project_id: p.project_id,
      task_id: p.task_id,
      label: p.task ? `${p.project} · ${p.task}` : p.project,
      minutes: String(Math.round(p.seconds / 60)),
    })),
  };
}

/** Only the parts that differ from the day as it stands. */
function changeOf(day: DayState, f: Form): DayChange {
  const change: DayChange = {};
  const now = formOf(day);
  const sessions = f.sessions.map(({ id, in: i, out }) => ({ ...(id ? { id } : {}), in: i, out }));
  const credits = f.credits.map(({ id, hours, category, note }) => ({
    ...(id ? { id } : {}),
    hours: Number(hours),
    category,
    note: note?.trim() || null,
  }));
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  if (
    !same(
      sessions,
      now.sessions.map(({ id, in: i, out }) => ({ id, in: i, out })),
    )
  )
    change.sessions = sessions;
  if (
    !same(
      credits,
      now.credits.map(({ id, hours, category, note }) => ({
        id,
        hours: Number(hours),
        category,
        note: note?.trim() || null,
      })),
    )
  )
    change.credits = credits;
  if (
    !same(
      f.projects.map((p) => p.minutes),
      now.projects.map((p) => p.minutes),
    )
  )
    change.projects = f.projects.map((p) => ({
      project_id: p.project_id,
      task_id: p.task_id,
      seconds: Math.max(0, Math.round(Number(p.minutes) * 60)),
    }));
  return change;
}

const section: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-2)',
};
const heading: CSSProperties = {
  margin: 0,
  fontSize: 'var(--hf-text-xs)',
  fontWeight: 'var(--hf-weight-medium)',
  color: 'var(--hf-ink-2)',
  letterSpacing: 'var(--hf-track-eyebrow)',
  textTransform: 'uppercase',
};
const row: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--hf-space-2)',
  alignItems: 'flex-end',
};
const quiet: CSSProperties = { margin: 0, fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' };

export function EditDayDialog({
  profileId,
  name,
  date: initialDate,
  pickDate,
  decide,
  canAmend,
  onClose,
  onDone,
}: {
  profileId: string;
  /** Whose day, for the words. Omit on your own. */
  name?: string;
  /** 'YYYY-MM-DD', the org day. */
  date: string;
  /** Offer a date field: a day with no clock activity has no row to open it from. */
  pickDate?: boolean;
  /** Deciding this waiting request instead of editing. */
  decide?: PendingDayEdit;
  /** HR: a locked day links to the Amend workspace. */
  canAmend?: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [date, setDate] = useState(initialDate);
  const [day, setDay] = useState<DayState | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [form, setForm] = useState<Form | null>(null);
  const [touched, setTouched] = useState(false);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<{
    at: 'sessions' | 'reason' | 'form';
    message: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    void listCategories().then(setCategories);
  }, []);
  useEffect(() => {
    let live = true;
    setDay(null);
    setFailed(null);
    void getDayState(profileId, date).then(({ data, error }) => {
      if (!live) return;
      if (error || !data) return setFailed(error?.message ?? 'no answer');
      setDay(data);
      setForm(formOf(data, decide?.proposed));
      setTouched(false);
    });
    return () => {
      live = false;
    };
  }, [profileId, date, attempt, decide]);

  const whose = name ? `${name}’s` : 'your';
  const mode = decide ? 'decide' : (day?.mode ?? 'read');
  const editable = !!day && day.mode !== 'read' && (!decide || day.mode === 'direct');
  const edit = (f: (prev: Form) => Form) => {
    setForm((p) => (p ? f(p) : p));
    setTouched(true);
    if (problem?.at !== 'reason') setProblem(null);
  };
  const setSession = (k: string, patch: Partial<SessionRow>) =>
    edit((p) => ({
      ...p,
      sessions: p.sessions.map((s) => (s.key === k ? { ...s, ...patch } : s)),
    }));
  const setCredit = (k: string, patch: Partial<CreditRow>) =>
    edit((p) => ({
      ...p,
      credits: p.credits.map((c) => {
        if (c.key !== k) return c;
        const next = { ...c, ...patch };
        // From and To only fill in Hours: hours are what is stored (#285).
        const span = 'from' in patch || 'to' in patch ? spanHours(next.from, next.to) : null;
        return span !== null && span > 0 ? { ...next, hours: String(span) } : next;
      }),
    }));

  const submit = async (approve = true) => {
    if (!day || !form || busy) return;
    const sp = sessionProblem(form.sessions);
    if (approve && sp) return setProblem({ at: 'sessions', message: sp });
    const change = changeOf(day, form);
    if (!decide && !Object.keys(change).length)
      return setProblem({ at: 'form', message: 'Nothing has changed yet.' });
    if (!decide && !reason.trim())
      return setProblem({
        at: 'reason',
        message: name
          ? `Say why: ${name} sees it with the change.`
          : 'Say why: your approver sees it.',
      });
    setBusy(true);
    setProblem(null);
    const res = decide
      ? await decideDayEdit(
          decide.id,
          approve,
          // Adjusted: the whole day as it now reads (an approver's revert of
          // one part of the ask must win over the ask).
          approve && touched
            ? {
                sessions: form.sessions.map(({ id, in: i, out }) => ({
                  ...(id ? { id } : {}),
                  in: i,
                  out,
                })),
                credits: form.credits.map(({ id, hours, category, note }) => ({
                  ...(id ? { id } : {}),
                  hours: Number(hours),
                  category,
                  note: note?.trim() || null,
                })),
                ...(change.projects ? { projects: change.projects } : {}),
              }
            : null,
          reason.trim() || null,
        )
      : day.mode === 'direct'
        ? await editDay(profileId, date, change, reason.trim())
        : await requestDayEdit(date, change, reason.trim());
    setBusy(false);
    if (res.error) return setProblem({ at: 'form', message: res.error.message });
    toast.push({
      tone: 'success',
      message: decide
        ? `${approve ? (touched ? 'Approved with your changes' : 'Approved') : 'Declined'}: ${whose} ${fmtDayKey(date)}. They’ve been told.`
        : day.mode === 'direct'
          ? `Changed ${whose} ${fmtDayKey(date)}. They’ve been told.`
          : `Asked to change ${fmtDayKey(date)}. Your manager or HR decides.`,
    });
    onDone();
    onClose();
  };

  const title =
    mode === 'decide'
      ? `Review ${whose} change`
      : mode === 'direct'
        ? `Edit ${whose} day`
        : mode === 'request'
          ? 'Ask to change your day'
          : `${name ? `${name}’s` : 'Your'} day`;
  const open = day?.sessions.filter((s) => s.status !== 'logged') ?? [];
  const pendingCredits = day?.credits.filter((c) => c.status === 'pending') ?? [];
  const worked = form ? spanSeconds(form.sessions.filter((s) => s.in && s.out)) : 0;

  return (
    <Dialog
      open
      size="md"
      title={title}
      onClose={onClose}
      dirty={touched || reason.trim() !== ''}
      footer={(requestClose) =>
        mode === 'decide' ? (
          <>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
            <Button variant="secondary" disabled={busy || !day} onClick={() => void submit(false)}>
              Decline
            </Button>
            <Button
              variant="primary"
              icon="check"
              loading={busy}
              disabled={!editable}
              onClick={() => void submit(true)}
            >
              {touched ? 'Approve with changes' : 'Approve'}
            </Button>
          </>
        ) : mode === 'read' ? (
          <Button variant="secondary" onClick={requestClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
            <Button variant="primary" icon="check" loading={busy} onClick={() => void submit()}>
              {mode === 'direct' ? 'Save changes' : 'Send request'}
            </Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        {pickDate && !decide ? (
          <DateField label="Day" value={date} onChange={(v) => v && setDate(v)} />
        ) : (
          <p style={{ ...quiet, color: 'var(--hf-ink)' }}>{fmtDayKey(date, { weekday: true })}</p>
        )}

        {failed ? (
          <LoadError what="this day" onRetry={() => setAttempt((n) => n + 1)} />
        ) : !day || !form ? (
          <div role="status" style={quiet}>
            Loading…
          </div>
        ) : (
          <>
            {day.locked && day.mode === 'read' && (
              <InlineStatus tone="info">
                This day is in a payroll month that is locked, so it can’t change here.
                {canAmend ? (
                  <>
                    {' '}
                    It can change only inside an amendment.{' '}
                    <Link to="/hr?view=payroll">Open Amend</Link>
                  </>
                ) : (
                  ' HR can change it inside an amendment.'
                )}
              </InlineStatus>
            )}
            {day.locked && day.amend_open && (
              <InlineStatus tone="warning">
                The amendment window is open: this change lands in a locked payroll month.
              </InlineStatus>
            )}
            {decide && (
              <div style={section}>
                <p style={quiet}>
                  Asked {fmtRelative(decide.created_at)}: “{decide.reason}”
                </p>
                <p style={quiet}>
                  Was <Mono>{sessionsText(decide.before.sessions)}</Mono>
                  {decide.proposed.sessions && (
                    <>
                      {' '}
                      · asked for <Mono>{sessionsText(decide.proposed.sessions)}</Mono>
                    </>
                  )}
                </p>
                {day.mode !== 'direct' && (
                  <InlineStatus tone="info">
                    You can decline this, but not approve it: the day can’t change from here.
                  </InlineStatus>
                )}
              </div>
            )}
            {!decide && day.pending && (
              <InlineStatus tone="info">
                {day.mode === 'request'
                  ? 'You already asked to change this day. Sending again replaces that request.'
                  : 'They asked to change this day. That request is waiting in Requests.'}
              </InlineStatus>
            )}

            <div style={section}>
              <h3 style={heading}>Clock times</h3>
              {open.map((s) => (
                <p key={s.id} style={quiet}>
                  <Mono>{s.in} → now</Mono> · still checked in, so it can’t change yet
                </p>
              ))}
              {form.sessions.length === 0 && open.length === 0 && (
                <p style={quiet}>No time on this day.</p>
              )}
              {form.sessions.map((s, i) => (
                <div key={s.key} style={row}>
                  <Field label={`Check-in ${i + 1}`} style={{ flex: '1 1 7rem' }}>
                    <TextInput
                      type="time"
                      mono
                      value={s.in}
                      disabled={!editable}
                      onChange={(v) => setSession(s.key, { in: v })}
                    />
                  </Field>
                  <Field label={`Check-out ${i + 1}`} style={{ flex: '1 1 7rem' }}>
                    <TextInput
                      type="time"
                      mono
                      value={s.out}
                      disabled={!editable}
                      onChange={(v) => setSession(s.key, { out: v })}
                    />
                  </Field>
                  {editable && (
                    <IconButton
                      icon="x"
                      size={44}
                      label={`Remove session ${i + 1}`}
                      onClick={() =>
                        edit((p) => ({ ...p, sessions: p.sessions.filter((x) => x.key !== s.key) }))
                      }
                    />
                  )}
                </div>
              ))}
              {editable && (
                <div>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="plus"
                    onClick={() =>
                      edit((p) => ({
                        ...p,
                        sessions: [...p.sessions, { key: key(), in: '', out: '' }],
                      }))
                    }
                  >
                    Add a session
                  </Button>
                </div>
              )}
              {problem?.at === 'sessions' && (
                <InlineStatus tone="error">{problem.message}</InlineStatus>
              )}
              {form.sessions.length > 0 && (
                <p style={{ ...quiet, fontSize: 'var(--hf-text-xs)' }}>
                  <Mono>{fmtDuration(worked, 'hm')}</Mono> between check-ins and check-outs. Breaks
                  inside a session are taken off.
                </p>
              )}
            </div>

            <div style={section}>
              <h3 style={heading}>Credited hours</h3>
              {pendingCredits.map((c) => (
                <p key={c.id} style={quiet}>
                  <Mono>+{fmtHours(c.hours)}</Mono> waiting as a claim of its own
                </p>
              ))}
              {form.credits.length === 0 && pendingCredits.length === 0 && (
                <p style={quiet}>None.</p>
              )}
              {form.credits.map((c, i) => (
                <div key={c.key} style={row}>
                  <Field label="From" style={{ flex: '1 1 6rem' }}>
                    <TextInput
                      type="time"
                      mono
                      value={c.from}
                      disabled={!editable}
                      onChange={(v) => setCredit(c.key, { from: v })}
                    />
                  </Field>
                  <Field label="To" style={{ flex: '1 1 6rem' }}>
                    <TextInput
                      type="time"
                      mono
                      value={c.to}
                      disabled={!editable}
                      onChange={(v) => setCredit(c.key, { to: v })}
                    />
                  </Field>
                  <Field label="Hours" style={{ flex: '1 1 5rem' }}>
                    <TextInput
                      type="number"
                      mono
                      step="0.5"
                      min="0.5"
                      max="24"
                      value={c.hours}
                      disabled={!editable}
                      onChange={(v) => setCredit(c.key, { hours: v })}
                    />
                  </Field>
                  <Field label="For" style={{ flex: '2 1 9rem' }}>
                    {(control) => (
                      <Select
                        {...control}
                        value={c.category}
                        options={categories.map((k) => ({ value: k.id, label: k.label }))}
                        onChange={(v) => editable && setCredit(c.key, { category: v })}
                      />
                    )}
                  </Field>
                  {editable && (
                    <IconButton
                      icon="x"
                      size={44}
                      label={`Remove credit ${i + 1}`}
                      onClick={() =>
                        edit((p) => ({ ...p, credits: p.credits.filter((x) => x.key !== c.key) }))
                      }
                    />
                  )}
                </div>
              ))}
              {editable && (
                <div>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="plus"
                    onClick={() =>
                      edit((p) => ({
                        ...p,
                        credits: [
                          ...p.credits,
                          {
                            key: key(),
                            from: '',
                            to: '',
                            hours: '',
                            category: categories[0]?.id ?? '',
                            note: null,
                          },
                        ],
                      }))
                    }
                  >
                    Credit hours
                  </Button>
                </div>
              )}
            </div>

            {form.projects.length > 0 && (
              <div style={section}>
                <h3 style={heading}>Project time</h3>
                <p style={{ ...quiet, fontSize: 'var(--hf-text-xs)' }}>
                  Follows the clock times by itself.{' '}
                  {day.mode === 'direct' &&
                    'Set the minutes to move time between projects; the rest stays unallocated.'}
                </p>
                {form.projects.map((p) => (
                  <div key={`${p.project_id}:${p.task_id}`} style={row}>
                    <Field label={p.label} style={{ flex: '1 1 12rem' }}>
                      <TextInput
                        type="number"
                        mono
                        min="0"
                        value={p.minutes}
                        disabled={day.mode !== 'direct'}
                        trailing={<span style={YOURS}>min</span>}
                        onChange={(v) =>
                          edit((f) => ({
                            ...f,
                            projects: f.projects.map((x) => (x === p ? { ...x, minutes: v } : x)),
                          }))
                        }
                      />
                    </Field>
                  </div>
                ))}
              </div>
            )}

            {mode !== 'read' && (
              <Field
                label={decide ? 'Note to them' : 'Why'}
                required={!decide}
                error={problem?.at === 'reason' ? problem.message : undefined}
                hint={
                  decide
                    ? 'Optional. They see it with your decision.'
                    : name
                      ? `${name} sees this with the change, and it is kept on record.`
                      : 'Your approver sees this, and it is kept on record.'
                }
              >
                <TextArea
                  value={reason}
                  onChange={setReason}
                  placeholder={
                    decide
                      ? 'e.g. The evening hour wasn’t work'
                      : 'e.g. Left at 6, forgot to check out'
                  }
                />
              </Field>
            )}
            {problem?.at === 'form' && <InlineStatus tone="error">{problem.message}</InlineStatus>}
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Waiting whole-day requests, as rows for a queue (People → Requests, and a
 * manager's My space). Review opens the dialog in decide mode.
 */
export function DayEditDecisions({
  rows,
  uid,
  canAmend,
  onChanged,
}: {
  rows: readonly PendingDayEdit[];
  uid: string;
  canAmend?: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState<PendingDayEdit | null>(null);
  if (!rows.length) return null;
  return (
    <>
      {rows.map((r) => (
        <DecisionRow
          key={r.id}
          flush
          name={r.full_name ?? 'Someone'}
          when={fmtDayKey(r.work_date, { weekday: true })}
          what={
            <>
              <KindTag kind="day" />
              <Mono>{sessionsText(r.before.sessions)}</Mono>
              {r.proposed.sessions && (
                <>
                  <span aria-hidden="true">→</span>
                  <Mono>{sessionsText(r.proposed.sessions)}</Mono>
                </>
              )}
            </>
          }
          detail={r.reason}
          filed={fmtRelative(r.created_at)}
          aside={
            r.profile_id === uid ? (
              <span style={YOURS}>yours — another approver decides</span>
            ) : (
              <Button size="sm" variant="secondary" className="hf-hit" onClick={() => setOpen(r)}>
                Review
              </Button>
            )
          }
        />
      ))}
      {open && (
        <EditDayDialog
          profileId={open.profile_id}
          name={open.full_name ?? 'Someone'}
          date={open.work_date}
          decide={open}
          canAmend={canAmend}
          onClose={() => setOpen(null)}
          onDone={onChanged}
        />
      )}
    </>
  );
}

/**
 * My space, for someone people report to: the whole-day changes their reports
 * asked for. Renders nothing when there are none. No attention badge, like
 * the compensated-hours queue beside it: /me has no module to hang one on,
 * and the request's notice already brought them here.
 */
export function TeamDayEdits({ uid }: { uid: string }) {
  const [rows, setRows] = useState<PendingDayEdit[] | null>([]);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    void listPendingDayEdits().then((r) => live && setRows(r));
    return () => {
      live = false;
    };
  }, [nonce]);
  // HR reads every request; this card is only the viewer's own reports.
  const mine = rows?.filter((r) => r.manager_id === uid) ?? [];
  if (rows && !mine.length) return null;
  return (
    <Card label="Day changes your team asked for">
      {rows === null ? (
        <LoadError what="your team’s day change requests" onRetry={() => setNonce((n) => n + 1)} />
      ) : (
        <DayEditDecisions rows={mine} uid={uid} onChanged={() => setNonce((n) => n + 1)} />
      )}
    </Card>
  );
}
